import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  dlLog,
  exit,
  notifyCheckInReady,
  notifyCheckInUnavailable,
  notifyCheckInFailed,
  onCheckInShow,
  toast,
  waitBridge
} from '@/utils/bridge'
import { CheckInDay, CheckInInfo, getInfo, popupAck, sign } from './services'
import shellUrl from './images/shell.webp'
import closeUrl from './images/close.webp'
import rewardLightUrl from './images/reward-light.svg'
import rewardNameUrl from './images/reward-name.svg'
import './index.scss'

const TAG = 'CheckIn'
const STATUS_SIGNED = 2
const REWARD_ASSET = 1
const REWARD_PROP = 2
const REWARD_CLOSE_SECONDS = 5

function readFiniteNumber(value: unknown): number | null {
  if (typeof value === 'number' && Number.isFinite(value)) return value
  if (typeof value === 'string' && value.trim() !== '') {
    const parsed = Number(value)
    if (Number.isFinite(parsed)) return parsed
  }
  return null
}

function readRewardType(day: number, value: unknown): number {
  const type = readFiniteNumber(value)
  if (type === 0 || type === REWARD_ASSET || type === REWARD_PROP) return type
  dlLog('warn', TAG, `reward type invalid day:${day} type:${value ?? ''}`)
  return 0
}

function readRewardAmount(day: number, value: unknown): number {
  const amount = readFiniteNumber(value)
  if (amount != null) return amount
  dlLog('warn', TAG, `reward amount invalid day:${day} amount:${value ?? ''}`)
  return Number.NaN
}

function formatRewardText(
  rewardType: number,
  rewardAmount: number,
  translate: (key: string, options?: { amount: number }) => string
): string | null {
  if (!Number.isFinite(rewardAmount)) return null
  if (rewardType === REWARD_ASSET) return `x${rewardAmount}`
  if (rewardType === REWARD_PROP) {
    if (rewardAmount > 0) return translate('CheckIn.Days', { amount: rewardAmount })
    return translate('CheckIn.Forever')
  }
  return null
}

function readTodayReward(data: CheckInInfo): CheckInDay | null {
  const days = Array.isArray(data.days) ? data.days : []
  const today = days.find((item) => item.isToday === 1)
  if (!today) return null
  return {
    day: today.day,
    status: today.status,
    isToday: 1,
    rewardIcon: today.rewardIcon || '',
    rewardType: readRewardType(today.day, today.rewardType),
    rewardAmount: readRewardAmount(today.day, today.rewardAmount),
    rewardName: typeof today.rewardName === 'string' ? today.rewardName : ''
  }
}

function CheckInPage() {
  const { t } = useTranslation()
  const [info, setInfo] = useState<CheckInInfo>()
  const [signing, setSigning] = useState(false)
  const [armedReward, setArmedReward] = useState<CheckInDay>()
  const [rewardOpen, setRewardOpen] = useState(false)
  const [rewardSeconds, setRewardSeconds] = useState(REWARD_CLOSE_SECONDS)
  const visibleRef = useRef(false)
  const showTokenRef = useRef(0)
  const ackedTokenRef = useRef(-1)
  const signingRef = useRef(false)
  const rewardArmedRef = useRef<CheckInDay | null>(null)
  const rewardOpenRef = useRef(false)
  const rewardFinishedRef = useRef(false)
  const rewardTimerRef = useRef<number>()

  const applyInfo = useCallback((data: CheckInInfo) => {
    const days = (Array.isArray(data.days) ? data.days : []).map((item) => ({
      day: item.day,
      status: item.status,
      isToday: item.isToday,
      rewardIcon: item.rewardIcon,
      rewardType: readRewardType(item.day, item.rewardType),
      rewardAmount: readRewardAmount(item.day, item.rewardAmount),
      rewardName: item.rewardName
    }))
    days.sort((a, b) => a.day - b.day)
    dlLog('info', TAG, `rewards ${days.map((item) => `${item.day}:${item.rewardType}/${item.rewardAmount}`).join(' ')}`)
    setInfo({
      eligible: data.eligible,
      endReason: data.endReason,
      timezone: data.timezone,
      serverTime: data.serverTime,
      windowEndTime: data.windowEndTime,
      streak: data.streak,
      todayChecked: data.todayChecked,
      days
    })
  }, [])

  const acknowledge = useCallback(async (token: number) => {
    if (ackedTokenRef.current === token) return
    ackedTokenRef.current = token
    dlLog('info', TAG, 'popupAck start')
    try {
      const res = await popupAck()
      if (!res || res.code !== 200) {
        ackedTokenRef.current = -1
        dlLog('warn', TAG, `popupAck fail code:${res?.code ?? -1}`)
        return
      }
      dlLog('info', TAG, 'popupAck success')
    } catch {
      ackedTokenRef.current = -1
      dlLog('error', TAG, 'popupAck error')
    }
  }, [])

  const loadInfo = useCallback(async (showToken?: number) => {
    dlLog('info', TAG, 'getInfo start')
    try {
      const res = await getInfo()
      if (!res || res.code !== 200 || !res.data) {
        dlLog('warn', TAG, `getInfo fail code:${res?.code ?? -1}`)
        notifyCheckInFailed()
        return
      }
      const data = res.data
      dlLog(
        'info',
        TAG,
        `getInfo eligible:${data.eligible} streak:${data.streak} todayChecked:${data.todayChecked} reason:${data.endReason}`
      )
      if (data.eligible !== 1) {
        notifyCheckInUnavailable()
        return
      }
      applyInfo(data)
      notifyCheckInReady()
      if (visibleRef.current && showToken != null) {
        acknowledge(showToken)
      }
    } catch {
      dlLog('error', TAG, 'getInfo error')
      notifyCheckInFailed()
    }
  }, [acknowledge, applyInfo])

  useEffect(() => {
    const html = document.documentElement
    const body = document.body
    const root = document.getElementById('root')
    const previousHtml = html.style.background
    const previousBody = body.style.background
    const previousRoot = root?.style.background ?? ''
    html.style.background = 'transparent'
    body.style.background = 'transparent'
    if (root) root.style.background = 'transparent'
    dlLog('info', TAG, 'page open')
    let cancelled = false
    const start = async () => {
      if (!window.bridge && (window.isAndroid || window.isIOS)) {
        await waitBridge()
      }
      if (cancelled) return
      onCheckInShow(() => {
        visibleRef.current = true
        showTokenRef.current += 1
        dlLog('info', TAG, `show token:${showTokenRef.current}`)
        loadInfo(showTokenRef.current)
      })
      loadInfo()
    }
    start()
    return () => {
      cancelled = true
      html.style.background = previousHtml
      body.style.background = previousBody
      if (root) root.style.background = previousRoot
    }
  }, [loadInfo])

  const today = info?.days?.find((item) => item.isToday === 1)
  const canSign = !!info && info.todayChecked !== 1 && today?.status !== STATUS_SIGNED && !signing

  const onSign = async () => {
    if (signingRef.current || !canSign) {
      dlLog('info', TAG, 'sign ignored')
      return
    }
    signingRef.current = true
    setSigning(true)
    dlLog('info', TAG, `sign click day:${today?.day ?? 0}`)
    try {
      const res = await sign()
      if (!res || res.code !== 200 || !res.data) {
        dlLog('warn', TAG, `sign fail code:${res?.code ?? -1}`)
        toast(t('Common.SystemError'))
        return
      }
      const data = res.data
      dlLog('info', TAG, `sign result:${data.result} rewardStatus:${data.rewardStatus} eligible:${data.eligible}`)
      if (data.eligible !== 1) {
        notifyCheckInUnavailable()
        return
      }
      applyInfo(data)
      if (data.result === 2) {
        toast(t('CheckIn.Already'))
      } else if (data.result === 4) {
        toast(t('CheckIn.Risk'))
      } else if (data.result === 3) {
        toast(t('CheckIn.Unavailable'))
        notifyCheckInUnavailable()
      } else if (data.result === 1 && data.rewardStatus === 2) {
        rewardArmedRef.current = null
        setArmedReward(undefined)
        toast(t('CheckIn.Pending'))
      } else if (data.result === 1) {
        const todayReward = readTodayReward(data)
        if (!todayReward) {
          rewardArmedRef.current = null
          setArmedReward(undefined)
          dlLog('warn', TAG, 'reward skipped reason:no-today')
        } else {
          rewardArmedRef.current = todayReward
          setArmedReward(todayReward)
          dlLog(
            'info',
            TAG,
            `reward ready day:${todayReward.day} type:${todayReward.rewardType} amount:${todayReward.rewardAmount}`
          )
        }
      }
    } catch {
      dlLog('error', TAG, 'sign error')
      toast(t('Common.NetworkError'))
    } finally {
      signingRef.current = false
      setSigning(false)
    }
  }

  const finishReward = useCallback((reason: 'receive' | 'countdown') => {
    if (rewardFinishedRef.current) return
    rewardFinishedRef.current = true
    if (rewardTimerRef.current != null) {
      window.clearInterval(rewardTimerRef.current)
      rewardTimerRef.current = undefined
    }
    dlLog('info', TAG, `reward close reason:${reason}`)
    exit()
  }, [])

  const openReward = () => {
    const reward = rewardArmedRef.current
    if (!reward || rewardOpenRef.current) return
    rewardOpenRef.current = true
    rewardFinishedRef.current = false
    setRewardSeconds(REWARD_CLOSE_SECONDS)
    setRewardOpen(true)
    dlLog('info', TAG, `reward show day:${reward.day} type:${reward.rewardType} amount:${reward.rewardAmount}`)
  }

  useEffect(() => {
    if (!rewardOpen) return
    let left = REWARD_CLOSE_SECONDS
    const timer = window.setInterval(() => {
      left -= 1
      if (left <= 0) {
        window.clearInterval(timer)
        finishReward('countdown')
        return
      }
      setRewardSeconds(left)
    }, 1000)
    rewardTimerRef.current = timer
    return () => {
      window.clearInterval(timer)
      if (rewardTimerRef.current === timer) rewardTimerRef.current = undefined
    }
  }, [rewardOpen, finishReward])

  const onClose = () => {
    if (rewardOpenRef.current) return
    if (rewardArmedRef.current) {
      dlLog('info', TAG, 'close reward')
      openReward()
      return
    }
    dlLog('info', TAG, 'close')
    exit()
  }

  const onBlankClick = (event: React.MouseEvent<HTMLDivElement>) => {
    if (rewardOpenRef.current) return
    const target = event.target as HTMLElement
    if (target.closest('.pg-check-in__hit, .pg-check-in__card, .pg-check-in__title, .pg-check-in__subtitle, .pg-check-in__action, .pg-check-in__close')) {
      return
    }
    if (rewardArmedRef.current) {
      dlLog('info', TAG, 'blank reward')
      openReward()
      return
    }
    dlLog('info', TAG, 'blank close')
    exit()
  }

  const onReceive = () => {
    finishReward('receive')
  }

  const title = t('CheckIn.Title')
  const rewardTitle = t('CheckIn.RewardTitle')
  const checkedToday = info?.todayChecked === 1
  const shownReward = rewardOpen ? armedReward : undefined
  const shownRewardText = shownReward
    ? formatRewardText(shownReward.rewardType, shownReward.rewardAmount, t)
    : null

  return (
    <div className={`pg-check-in${rewardOpen ? ' is-reward' : ''}`} onClick={onBlankClick}>
      {shownReward ? (
        <div className="pg-check-in__frame">
          <img className="pg-check-in__reward-light" src={rewardLightUrl} alt="" />
          <div className={`pg-check-in__reward-title${rewardTitle.length > 18 ? ' is-compact' : ''}`}>
            {rewardTitle}
          </div>
          {shownReward.rewardIcon
            ? <img className="pg-check-in__reward-icon" src={shownReward.rewardIcon} alt="" />
            : <span className="pg-check-in__reward-icon" />}
          <img className="pg-check-in__reward-name-bg" src={rewardNameUrl} alt="" />
          <div className={`pg-check-in__reward-name${shownReward.rewardName.length > 16 ? ' is-compact' : ''}`}>
            {shownReward.rewardName}
          </div>
          {shownRewardText && (
            <div className={`pg-check-in__reward-amount${shownRewardText.length > 10 ? ' is-compact' : ''}`}>
              {shownRewardText}
            </div>
          )}
          <div className={`pg-check-in__reward-subtitle${shownRewardText ? ' is-shifted' : ''}`}>
            {t('CheckIn.Subtitle')}
          </div>
          <button className="pg-check-in__reward-receive" type="button" onClick={onReceive}>
            <span>{t('CheckIn.Receive')}</span>
            <span className="pg-check-in__reward-seconds">{rewardSeconds}s</span>
          </button>
        </div>
      ) : (
      <div className="pg-check-in__frame">
        <img className="pg-check-in__shell" src={shellUrl} alt="" />
        <div className="pg-check-in__hit pg-check-in__hit--panel" />
        <div className="pg-check-in__hit pg-check-in__hit--seven" />
        <div className="pg-check-in__title">
          <span className="pg-check-in__title-text">
            <span className="pg-check-in__title-stroke" aria-hidden="true">{title}</span>
            <span className="pg-check-in__title-shadow" aria-hidden="true">{title}</span>
            <span className="pg-check-in__title-fill">{title}</span>
          </span>
        </div>
        {(info?.days ?? []).map((item) => {
          const signed = item.status === STATUS_SIGNED
          const wide = item.day === 7
          const rewardText = formatRewardText(item.rewardType, item.rewardAmount, t)
          return (
            <div
              className={`pg-check-in__card ${signed ? 'is-signed' : 'is-open'} ${wide ? 'is-wide' : ''} is-day-${item.day}`}
              key={item.day}
            >
              {signed && <span className="pg-check-in__fire" />}
              <div className="pg-check-in__body">
                <div className="pg-check-in__day">{signed ? '' : t('CheckIn.Day', { day: item.day })}</div>
                {item.rewardIcon
                  ? <img className="pg-check-in__icon" src={item.rewardIcon} alt="" />
                  : <span className="pg-check-in__icon" />}
                {rewardText && (
                  <div className={`pg-check-in__amount ${signed ? 'is-signed' : ''} ${rewardText.length > 10 ? 'is-compact' : ''}`}>
                    {rewardText}
                  </div>
                )}
              </div>
            </div>
          )
        })}
        <div className="pg-check-in__subtitle">{t('CheckIn.Subtitle')}</div>
        <button
          className={`pg-check-in__action ${checkedToday ? 'is-done' : ''}`}
          type="button"
          disabled={!canSign}
          onClick={onSign}
        >
          {checkedToday ? t('CheckIn.Already') : t('CheckIn.Action')}
        </button>
        <button className="pg-check-in__close" type="button" onClick={onClose} aria-label="close">
          <img className="pg-check-in__close-icon" src={closeUrl} alt="" />
        </button>
      </div>
      )}
    </div>
  )
}

export default CheckInPage
